import { useEffect, useMemo, useRef, useState } from "react";

import './benchmark-creation-goals.css';

import DateRangeDropdown, { DateRange, DateRangeDropdownHandle } from "../Filters/DateRangeDropdown";
import SearchableStringListDropdown, { SearchableStringListDropdownHandle } from "../Filters/SearchableStringListDropdown";

import Editable from "../Icons/Editable";
import LargeX from "../Icons/LargeX";
import ResetFilter from "../Icons/ResetFilter";

import { Sort } from "../../enums/Sort";

import { isIsoDateInRange } from '../../functions/TimeHelpers';

import { BenchmarkGoal } from "../../types/Quality/Benchmark";
import ExclamationTriangle from "../Icons/ExclamationTriangle";


type BenchmarkCreationGoalsTableProps = {
    goalList?: BenchmarkGoal[];
    onGoalClick: (id: number) => void;
    onDeleteClick?: (id: number) => void;
    canEdit?: boolean;
};

const BenchmarkCreationGoalsTable = ({
    goalList = [],
    onGoalClick,
    onDeleteClick = () => {},
    canEdit,
}: BenchmarkCreationGoalsTableProps) => {

    const [tradeFilter, setTradeFilter] = useState<string | undefined>()
    const [activityFilter, setActivityFilter] = useState<string | undefined>()
    const [dateFilter, setDateFilter] = useState<DateRange>({})

    const [filteredList, setFilteredList] = useState<BenchmarkGoal[]>(goalList)

    const tradeRef = useRef<SearchableStringListDropdownHandle>(null);
    const activityRef = useRef<SearchableStringListDropdownHandle>(null);
    const tierRef = useRef<SearchableStringListDropdownHandle>(null);
    const dateRef = useRef<DateRangeDropdownHandle>(null);

    const tradeOptions = useMemo(() => 
        Array.from(new Set(
            goalList
                .map(g => (g.trade?.name))
                .filter(Boolean)
        )).sort((a, b) => a.localeCompare(b))
    , [goalList]);

    const activityOptions = useMemo(() => 
        Array.from(new Set(
            goalList
                .map(g => (g.activity?.name))
                .filter(Boolean)
        )).sort((a, b) => a.localeCompare(b))
    , [goalList]);

    useEffect(() => {
        if (!goalList) return;

        const hasDateSort = dateFilter?.sort !== undefined;

        const filtered = goalList.filter(g => {
            const matchesTrade = tradeFilter ? g.trade?.name === tradeFilter : true;
            const matchesActivity = activityFilter ? g.activity?.name === activityFilter : true;
            const matchesDate = isIsoDateInRange(g.targetCompletionDate, dateFilter.start, dateFilter.end);

            return matchesTrade && matchesActivity && matchesDate;
        })

        if (!hasDateSort) {
            setFilteredList(filtered);
            return;
        }

        const sorted = [...filtered].sort((a, b) => {
            const targetA = new Date(a.targetCompletionDate).getTime();
            const targetB = new Date(b.targetCompletionDate).getTime();

            const cmp = targetA - targetB;
            return dateFilter.sort === Sort.Ascending ? cmp : -cmp;
        });

        setFilteredList(sorted);

    }, [goalList, tradeFilter, activityFilter, dateFilter]);

    const resetFilters = () => {
        tradeRef.current?.clear();
        activityRef.current?.clear();
        tierRef.current?.clear();
        dateRef.current?.clear();
    };

    /**
     * This puts the building of what is displayed when no rows are available
     *   and allow for us to switch to the "list" render when needed
     * @returns No Table rows for the benchmark activities
     */
    const renderNoList = () => (
        <tr>
            <td className='px-2'></td>{/* Blank on Purpose */}
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'></td>{/* Blank on Purpose */}
        </tr>
    );

    const isDateExpired = (dateStr: string) => {
        const today = new Date();
        const targetDate = new Date(dateStr);
        return targetDate < today;
    }

    /**
     * This puts the building of what is displayed when row datas is available
     *   and allow for us to switch to the "list" render when needed
     * @returns No Table rows for the benchmark activities
     */
    const renderList = () => (
        filteredList.map((b: BenchmarkGoal) => (
            <tr key={`${b.id}`}>
                <td>{
                        isDateExpired(b.targetCompletionDate) && !b.completeDate
                        ?
                        <div
                            title='Target Completion Date has passed'
                        >
                            <ExclamationTriangle
                                size={20}
                                strokeColor='red'
                                markColor='red'
                            />
                        </div>
                        : ''
                    }
                </td>
                <td>{b.trade?.name}</td>
                <td>{b.activity?.name}</td>
                <td>{b.targetCompletionDate}</td>
                <td>
                    {
                        canEdit && (
                            <div className="d-flex gap-1">
                                <button
                                    className='btn'
                                    onClick={() => onGoalClick(b.id)}
                                >
                                    <Editable />
                                </button>
                                <button
                                    className='btn'
                                    onClick={() => onDeleteClick(b.id)}
                                >
                                    <LargeX />
                                </button>
                            </div>
                        )
                    }
                </td>
            </tr>
    )));

    return (
        <div className="border rounded-2 shadow-sm benchmark-creation-goals">
            <div className="benchmark-creation-goals-controls border-bottom mb-1">
                <div></div>
                <div>
                    <SearchableStringListDropdown
                        label='Trade'
                        ref={tradeRef}
                        wrapperClassName='mb-2'
                        options={tradeOptions}
                        placeholder='Filter...'
                        onChange={(f: string | undefined) => setTradeFilter(f ?? '')}
                        clearable
                    />
                </div>
                <div>
                    <SearchableStringListDropdown
                        label='Activity'
                        ref={activityRef}
                        wrapperClassName='mb-2'
                        options={activityOptions}
                        placeholder='Filter...'
                        onChange={(f: string | undefined) => setActivityFilter(f ?? '')}
                        clearable
                    />
                </div>
                <div>
                    <DateRangeDropdown
                        label='Target Date'
                        ref={dateRef}
                        wrapperClassName='mb-2'
                        buttonWidthCh={36}
                        onChange={(d) => {
                            // add update of sorting here if needed
                            setDateFilter(d);
                        }}
                        sort={dateFilter.sort}
                        sortable={true}
                    />
                </div>
                <div>
                    <button
                        title='reset all filters'
                        className='benchmark-reset-btn mb-2 me-1'
                        onClick={() => resetFilters()}
                    >
                        <ResetFilter />
                    </button>
                </div>
            </div>
            <div className='benchmark-creation-goal-container'>
                <table className='benchmark-creation-goal-table'>
                    <colgroup>
                        <col />
                        <col />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className='benchmark-creation-goal-table-header' >{/* Notification Icons */}</th>
                            <th className='benchmark-creation-goal-table-header' >{/* Trade */}</th>
                            <th className='benchmark-creation-goal-table-header' >{/* Activity */}</th>
                            <th className='benchmark-creation-goal-table-header' >{/* Date */}</th>
                            <th className='benchmark-creation-goal-table-header' >{/* Button/Icons */}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList && filteredList.length > 0 ? renderList() : renderNoList()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BenchmarkCreationGoalsTable;